<template>
  <div ref="cardContainer" class="app-product-aid-card-stack">
    <AppProductAidSwipeCard
      v-for="(topic, index) in topics"
      :key="topic.id"
      :ref="(el) => setCardRef(el, index)"
      :image="topic.image"
      :alt="topic.alt"
      :is-active="index === activeIndex"
      :is-next="index === activeIndex + 1"
      :is-hidden="index < activeIndex || index > activeIndex + 1"
      :should-load-image="isWithinPreloadWindow(index)"
      :style="getCardStyle(index)"
      :like-opacity="index === activeIndex ? likeIndicatorOpacity : 0"
      :dislike-opacity="index === activeIndex ? dislikeIndicatorOpacity : 0"
    />
  </div>
</template>

<script setup lang="ts">
  import type { ComponentPublicInstance } from 'vue';
  import { useSwipe } from '@vueuse/core';
  import { gsap } from 'gsap';
  import type { ProductAidItem } from './AppProductAid.vue';
  import type AppProductAidSwipeCard from './AppProductAidSwipeCard.vue';

  const { topics, activeIndex, likeIndicatorOpacity, dislikeIndicatorOpacity } = defineProps<{
    topics: ProductAidItem[];
    activeIndex: number;
    likeIndicatorOpacity: number;
    dislikeIndicatorOpacity: number;
  }>();

  const emit = defineEmits<{
    like: [];
    dislike: [];
    'update:like-indicator-opacity': [value: number];
    'update:dislike-indicator-opacity': [value: number];
  }>();

  // How many cards ahead of the active one keep their image mounted. Cards past this
  // point render without an <img> at all: `display: none` does not stop a browser from
  // fetching an image, so rendering the full stack fired every topic image in one burst.
  const PRELOAD_AHEAD = 3;

  const cardContainer = ref<HTMLElement | null>(null);
  const cardRefs = ref<(InstanceType<typeof AppProductAidSwipeCard> | null)[]>([]);
  const isDragging = ref(false);
  const dragOffset = ref({ x: 0, y: 0 });
  // Monotonic: a card can only become active after being swiped past, so anything at or
  // below the watermark has already been fetched and stays mounted for the "terug" action.
  const preloadWatermark = ref(PRELOAD_AHEAD);

  watch(
    () => activeIndex,
    (index) => {
      preloadWatermark.value = Math.max(preloadWatermark.value, index + PRELOAD_AHEAD);
    },
  );

  function isWithinPreloadWindow(index: number) {
    return index <= preloadWatermark.value;
  }

  function setCardRef(el: Element | ComponentPublicInstance | null, index: number) {
    cardRefs.value[index] = el as InstanceType<typeof AppProductAidSwipeCard> | null;
  }

  function getCardElement(index: number) {
    return cardRefs.value[index]?.el;
  }

  function getCardStyle(index: number): Record<string, string | number> {
    const offset = index - activeIndex;

    if (offset < 0 || offset > 1) {
      return { display: 'none' };
    }

    const baseRotation = offset === 0 ? 0 : 3;
    const scale = offset === 0 ? 1 : 0.95;
    const translateY = offset * 10;
    const zIndex = 10 - offset;

    return {
      transform: `translateY(${translateY}px) rotate(${baseRotation}deg) scale(${scale})`,
      zIndex: String(zIndex),
    };
  }

  // Set up swipe detection on the card container
  useSwipe(cardContainer, {
    threshold: 10,
    onSwipe: (e) => {
      if (activeIndex >= topics.length) return;

      const activeCard = getCardElement(activeIndex);
      if (!activeCard) return;

      isDragging.value = true;

      // Get touch/mouse position relative to start
      const touch = e instanceof TouchEvent ? e.touches[0] : e;
      const rect = cardContainer.value?.getBoundingClientRect();
      if (!rect || !touch) return;

      const x = touch.clientX - rect.left - rect.width / 2;
      dragOffset.value = { x, y: 0 };

      // Update indicators
      emit('update:like-indicator-opacity', Math.max(0, Math.min(1, x / 100)));
      emit('update:dislike-indicator-opacity', Math.max(0, Math.min(1, -x / 100)));

      // Update card position
      gsap.set(activeCard, {
        x,
        rotation: x * 0.05,
      });
    },
    onSwipeEnd: (_e, direction) => {
      if (activeIndex >= topics.length) return;

      const activeCard = getCardElement(activeIndex);
      if (!activeCard) return;

      const threshold = 80;

      if (direction === 'right' && dragOffset.value.x > threshold) {
        // Handle like - isDragging is still true so handleLike won't animate again
        emit('like');
      } else if (direction === 'left' && dragOffset.value.x < -threshold) {
        // Handle dislike - isDragging is still true so handleDislike won't animate again
        emit('dislike');
      } else {
        // Snap back
        gsap.to(activeCard, {
          x: 0,
          rotation: 0,
          duration: 0.3,
          ease: 'power2.out',
        });
      }

      // Reset after handling
      isDragging.value = false;
      emit('update:like-indicator-opacity', 0);
      emit('update:dislike-indicator-opacity', 0);
      dragOffset.value = { x: 0, y: 0 };
    },
  });

  function animateCardOut(direction: 'left' | 'right', onComplete?: () => void) {
    const activeCard = getCardElement(activeIndex);
    if (!activeCard) {
      onComplete?.();
      return;
    }

    const xTarget = direction === 'right' ? 500 : -500;
    const rotation = direction === 'right' ? 30 : -30;

    gsap.to(activeCard, {
      x: xTarget,
      rotation,
      opacity: 0,
      duration: 0.4,
      ease: 'power2.in',
      onComplete: () => {
        gsap.set(activeCard, { x: 0, rotation: 0, opacity: 1 });
        onComplete?.();
      },
    });
  }

  function animateCardIn(index: number, fromDirection: 'left' | 'right') {
    const card = getCardElement(index);
    if (!card) return;

    gsap.fromTo(
      card,
      {
        x: fromDirection === 'right' ? 500 : -500,
        rotation: fromDirection === 'right' ? 30 : -30,
        opacity: 0,
      },
      {
        x: 0,
        rotation: 0,
        opacity: 1,
        duration: 0.4,
        ease: 'power2.out',
      },
    );
  }

  function resetCards() {
    // Topics are reshuffled on reset, so an index no longer maps to the image it did
    // before. Shrink the window back or the whole reshuffled stack would load at once.
    preloadWatermark.value = PRELOAD_AHEAD;

    cardRefs.value.forEach((component) => {
      const card = component?.el;
      if (card) {
        gsap.set(card, { x: 0, rotation: 0, opacity: 1 });
      }
    });
  }

  defineExpose({
    animateCardOut,
    animateCardIn,
    resetCards,
  });
</script>

<style lang="postcss" scoped>
  .app-product-aid-card-stack {
    touch-action: pan-y;

    position: relative;

    aspect-ratio: 50 / 75;
    width: 100%;
    max-width: 15rem;
    margin: 0 auto;

    perspective: 62.5rem;

    @media (width > 375px) {
      max-width: 18.5rem;
    }

    @media (width >= 768px) {
      max-width: 20rem;
    }
  }
</style>
